import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Plus, Minus, AlertTriangle } from 'lucide-react';
import { Card, Button, Textarea, Select, Input } from '../ui';
import { useUIStore, useServerStore, useAuthStore, useQuotaStore } from '../../store';
import { api } from '../../lib/api';

const DEADLINE_OPTIONS = [
  { value: '1_day', label: '1 day' },
  { value: '1_week', label: '1 week' },
  { value: '1_month', label: '1 month' },
  { value: '6_months', label: '6 months' },
  { value: '1_year', label: '1 year' },
  { value: '5_years', label: '5 years' },
  { value: '10_years', label: '10 years' },
  { value: 'custom', label: 'Custom date' },
];

function calculateDeadline(preset: string, customDate?: string): string {
  if (preset === 'custom' && customDate) return customDate;

  const now = new Date();
  const days: Record<string, number> = {
    '1_day': 1,
    '1_week': 7,
    '1_month': 30,
    '6_months': 180,
    '1_year': 365,
    '5_years': 1825,
    '10_years': 3650,
  };

  now.setDate(now.getDate() + (days[preset] || 7));
  return now.toISOString().split('T')[0];
}

export function CreatePrediction() {
  const { setView, showToast } = useUIStore();
  const { currentServer, currentChannel } = useServerStore();
  const { user } = useAuthStore();
  const { quota, canPost } = useQuotaStore();

  const [text, setText] = useState('I believe that ');
  const [confidence, setConfidence] = useState(100);
  const [deadlinePreset, setDeadlinePreset] = useState('1_week');
  const [customDate, setCustomDate] = useState('');
  const [invalidationThesis, setInvalidationThesis] = useState('');
  const [showInvalidation, setShowInvalidation] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      const length = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(length, length);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !currentServer) {
      showToast('Please sign in to create predictions', 'error');
      return;
    }

    if (!canPost()) {
      showToast('You have reached your prediction limit', 'error');
      return;
    }

    if (text.length < 15) {
      showToast('Prediction must be at least 15 characters', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      const deadline = calculateDeadline(deadlinePreset, customDate);

      const response = await api.predictions.create({
        text,
        confidence,
        deadline,
        invalidationThesis: showInvalidation ? invalidationThesis : undefined,
        isAnonymous,
        channelId: currentChannel?.id || 'general',
        serverId: currentServer.id,
      });

      if (response.success) {
        // Increment quota
        await api.quota.increment();
        showToast('Prediction created successfully', 'success');
        setView('feed');
      } else {
        showToast(response.error || 'Failed to create prediction', 'error');
      }
    } catch (error) {
      console.error('Failed to create prediction:', error);
      showToast('Failed to create prediction', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canCreate = canPost();
  const remainingPredictions = quota ? quota.limit - quota.used : 0;

  return (
    <div className="animate-in">
      <button
        onClick={() => setView('feed')}
        className="text-slate-400 hover:text-white flex items-center gap-2 text-xs font-bold mb-8 transition-colors tracking-widest uppercase"
      >
        <ArrowLeft size={16} /> Back to feed
      </button>

      <Card padding="lg" className="rounded-[2rem]">
        <h2 className="text-2xl font-bold mb-8 italic tracking-tight text-center">
          Create Prediction
        </h2>

        {!canCreate && (
          <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-start gap-3">
            <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-sm font-medium text-amber-400">
                Prediction limit reached
              </p>
              <p className="text-xs text-white/60 mt-1">
                Upgrade to premium for unlimited predictions
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <Textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={280}
            showCount
            placeholder="Enter your prediction..."
            className="h-32 text-lg font-bold italic"
            disabled={!canCreate}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="Deadline"
              value={deadlinePreset}
              onChange={(e) => setDeadlinePreset(e.target.value)}
              options={DEADLINE_OPTIONS}
              disabled={!canCreate}
            />

            <div className="relative">
              <Input
                label="Confidence"
                type="number"
                min={0}
                max={100}
                value={confidence}
                onChange={(e) => setConfidence(Number(e.target.value))}
                className="text-xl font-bold text-foretell-accent pr-10"
                disabled={!canCreate}
              />
              <span className="absolute right-4 top-[calc(50%+8px)] text-xl font-bold text-white/20">
                %
              </span>
            </div>
          </div>

          {deadlinePreset === 'custom' && (
            <Input
              label="Custom Date"
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              required
              disabled={!canCreate}
            />
          )}

          <div className="space-y-4">
            <button
              type="button"
              onClick={() => setShowInvalidation(!showInvalidation)}
              className="flex items-center gap-3 text-[10px] font-bold text-slate-400 hover:text-foretell-accent uppercase tracking-widest transition-colors"
              disabled={!canCreate}
            >
              <div className="w-5 h-5 rounded-md border border-white/10 flex items-center justify-center bg-black/40">
                {showInvalidation ? <Minus size={14} /> : <Plus size={14} />}
              </div>
              {showInvalidation ? 'Remove invalidation clause' : 'Add invalidation clause'}
            </button>

            {showInvalidation && (
              <Textarea
                value={invalidationThesis}
                onChange={(e) => setInvalidationThesis(e.target.value)}
                placeholder="What conditions would make this prediction void?"
                className="h-24"
                disabled={!canCreate}
              />
            )}
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
                className="w-4 h-4 rounded border-white/20 bg-black/40 text-foretell-accent focus:ring-foretell-accent"
                disabled={!canCreate}
              />
              <span className="text-sm text-white/60">Post anonymously</span>
            </label>
          </div>

          {quota && !quota.hasUnlimited && (
            <p className="text-xs text-white/40 text-center">
              {remainingPredictions} predictions remaining this month
            </p>
          )}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            loading={isSubmitting}
            disabled={!canCreate || text.length < 15}
          >
            Create Prediction
          </Button>
        </form>
      </Card>
    </div>
  );
}
